import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, message } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { disableMfa, enableMfa, getMfaStatus, startMfaSetup } from "../../services/api";

// Account settings -> Security -> Two-factor authentication (authenticator app).
type Step = "loading" | "off" | "setup" | "recovery" | "on" | "disable" | "signedOut";

const errorText = (error: any) =>
  String(error?.response?.data?.data?.message || error?.response?.data?.message || "Something went wrong. Please try again.");

const TwoFactorSettings = () => {
  const [step, setStep] = useState<Step>("loading");
  const [recoveryLeft, setRecoveryLeft] = useState(0);
  const [setup, setSetup] = useState<{ otpauthUrl: string; secret: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    setCode("");
    if (!localStorage.getItem("access_token")) {
      setStep("signedOut");
      return;
    }
    setStep("loading");
    try {
      const status = await getMfaStatus();
      setRecoveryLeft(Number(status?.recoveryCodesLeft) || 0);
      setStep(status?.enabled ? "on" : "off");
    } catch (e) {
      setError(errorText(e));
      setStep("off");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("Copied");
    } catch {
      /* clipboard blocked */
    }
  };

  const codeInput = (placeholder: string) => (
    <Input
      size="large"
      value={code}
      onChange={(event) => setCode(event.target.value)}
      autoComplete="one-time-code"
      maxLength={11}
      placeholder={placeholder}
      style={{ maxWidth: 260, textAlign: "center", letterSpacing: 4 }}
    />
  );

  return (
    <div>
      <h4>2-Factor Authentication (2FA)</h4>
      <p className="font_17x">
        Ask for a code from an authenticator app (Google Authenticator, Authy or similar) every time you sign in.
        This covers your Indexx account on Indexx, Bitcoin Yay and EMMM.
      </p>

      <div className="border-1x padding-2x margin-t-3x" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {step === "loading" && <span>Loading…</span>}

        {step === "signedOut" && <span>Sign in to manage two-factor authentication.</span>}

        {step === "off" && (
          <>
            <span>Authenticator app: <strong>off</strong></span>
            <Button
              type="primary"
              loading={busy}
              style={{ width: "fit-content" }}
              onClick={() =>
                run(async () => {
                  setSetup(await startMfaSetup());
                  setStep("setup");
                })
              }
            >
              Turn on
            </Button>
          </>
        )}

        {step === "setup" && setup && (
          <>
            <span>1. Scan this QR code with your authenticator app.</span>
            <div style={{ background: "#fff", padding: 12, borderRadius: 10, width: "fit-content" }}>
              <QRCodeSVG value={setup.otpauthUrl} size={180} />
            </div>
            <span style={{ fontSize: 13 }}>
              Can't scan it? Add this key in your app:{" "}
              <Button type="link" style={{ padding: 0, fontFamily: "monospace" }} onClick={() => copy(setup.secret)}>
                {setup.secret}
              </Button>
            </span>
            <span>2. Enter the 6-digit code your app shows.</span>
            {codeInput("123456")}
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                type="primary"
                loading={busy}
                disabled={code.replace(/\D/g, "").length !== 6}
                onClick={() =>
                  run(async () => {
                    const data = await enableMfa(code.replace(/\D/g, ""));
                    setRecoveryCodes(data?.recoveryCodes || []);
                    setCode("");
                    setStep("recovery");
                  })
                }
              >
                Verify and turn on
              </Button>
              <Button onClick={load}>Cancel</Button>
            </div>
          </>
        )}

        {step === "recovery" && (
          <>
            <strong>Two-factor authentication is now on.</strong>
            <span>
              Save these recovery codes somewhere safe. If you lose your phone, each one lets you sign in once. They
              won't be shown again.
            </span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 160px))", gap: 8 }}>
              {recoveryCodes.map((value) => (
                <code key={value} style={{ border: "1px solid #ddd", borderRadius: 6, padding: "6px 10px", textAlign: "center" }}>
                  {value}
                </code>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button onClick={() => copy(recoveryCodes.join("\n"))}>Copy codes</Button>
              <Button type="primary" onClick={load}>
                I've saved them
              </Button>
            </div>
          </>
        )}

        {step === "on" && (
          <>
            <span>Authenticator app: <strong>on</strong></span>
            <span style={{ fontSize: 13 }}>Recovery codes left: {recoveryLeft}</span>
            <Button style={{ width: "fit-content" }} onClick={() => { setCode(""); setStep("disable"); }}>
              Turn off
            </Button>
          </>
        )}

        {step === "disable" && (
          <>
            <span>Enter a code from your authenticator app, or a recovery code, to turn two-factor off.</span>
            {codeInput("123456 / XXXXX-XXXXX")}
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                type="primary"
                danger
                loading={busy}
                disabled={code.trim().length < 6}
                onClick={() =>
                  run(async () => {
                    await disableMfa(code.trim());
                    message.success("Two-factor authentication is off.");
                    await load();
                  })
                }
              >
                Turn off
              </Button>
              <Button onClick={load}>Cancel</Button>
            </div>
          </>
        )}

        {error ? <span style={{ color: "#e5484d" }}>{error}</span> : null}
      </div>
    </div>
  );
};

export default TwoFactorSettings;
